'use client';

import { useEffect } from 'react';

import { APP_VERSION_LABEL } from '@/shared/constants/appVersion';
import { claimRecovery, isAppAsset } from '@/shared/libs/assetRecovery';
import logger from '@/shared/libs/logger.client';

/**
 * Recharge la page quand un fragment de l'application a disparu du serveur (#1386).
 *
 * Voir `assetRecovery.ts` pour le mécanisme. Le symptôme, côté utilisateur, est muet : on clique,
 * l'adresse change, la page ne s'ouvre pas. Aucune erreur ne s'affiche — la transition attend un
 * fichier qui ne viendra jamais.
 *
 * **Deux écoutes, parce que l'échec arrive par deux chemins.** Le chargement d'un `<script>` échoue
 * en émettant un `error` qui **ne remonte pas** : il faut l'écouter en capture sur la fenêtre. Et
 * l'import différé d'un fragment rejette une promesse (`ChunkLoadError`) que personne n'attrape.
 *
 * Monté à la racine : le défaut ne connaît pas les espaces, et il frappe l'onglet resté ouvert
 * pendant une mise en service, quel que soit l'écran affiché.
 */
export function AssetRecovery() {
  useEffect(() => {
    const recover = (source: string, detail: string) => {
      if (!claimRecovery(Date.now(), window.sessionStorage)) {
        // Déjà tenté : le fragment manque durablement. Recharger encore ferait une boucle, et
        // c'est la trace qui compte alors — elle dit que le rechargement n'a pas suffi.
        logger.error('[asset] fragment introuvable, rechargement déjà tenté', {
          source,
          detail,
          version: APP_VERSION_LABEL,
          path: window.location.pathname,
        });
        return;
      }
      logger.warn('[asset] fragment introuvable, rechargement', {
        source,
        detail,
        version: APP_VERSION_LABEL,
        path: window.location.pathname,
      });
      // L'adresse a déjà changé : recharger ouvre donc la page demandée, pas la précédente.
      window.location.reload();
    };

    const onResourceError = (event: Event) => {
      const target = event.target;
      const url =
        target instanceof HTMLScriptElement
          ? target.src
          : target instanceof HTMLLinkElement
            ? target.href
            : null;
      if (!url || !isAppAsset(url)) return;
      recover('script', url);
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as { name?: string; message?: string } | undefined;
      if (reason?.name !== 'ChunkLoadError') return;
      recover('chunk', reason.message ?? 'ChunkLoadError');
    };

    // En capture : l'échec de chargement d'une ressource ne remonte pas jusqu'à la fenêtre.
    window.addEventListener('error', onResourceError, true);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onResourceError, true);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}

export default AssetRecovery;
